"use strict";
// Copyright © Veeam Software Group GmbH
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConfigurationBackupInteractor = void 0;
const rxjs_1 = require("rxjs");
require("@veeam/components");
const core_1 = require("@veeam/core");
const core_2 = require("../../core");
require("../../helpers");
require("../dialogs");
const ConfigurationBackupModel_1 = require("./ConfigurationBackupModel");
const types_1 = require("./types");
class ConfigurationBackupInteractor extends core_1.Interactor {
    constructor(model, loaders, config, presenters, listeners) {
        super();
        this.model = model;
        this.loaders = loaders;
        this.config = config;
        this.presenters = presenters;
        this.listeners = listeners;
        this.subscriptions = new rxjs_1.Subscription();
        this.onRepositoriesDialogShow = () => {
            this.presenters.onRepositoriesDialogActivate({
                repositoryId: this.form.getValue().repositoryId,
                onDone: (repositoryItem) => {
                    this.form.model.update((value) => {
                        value.repositoryId = repositoryItem.id;
                        value.repositoryName = repositoryItem.name;
                    });
                },
            });
        };
        this.onAvailableRestorePointsDialogShow = () => {
            this.presenters.onAvailableRestorePointsDialogActivate({
                onRemove: (id) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        yield this.loaders.removeRestorePointLoadableModel.load({ id });
                        this.presenters.onGoToSessionLogsDialogShow(this.config.goToSessionLogsDialogMessage);
                        const isLatestRestorePoint = this.model.getValue().restorePointId === id;
                        if (isLatestRestorePoint) {
                            yield this.loadCurrentSettings();
                        }
                    }
                    catch (e) {
                        this.presenters.onCatchError(e);
                    }
                }),
                onExport: (id) => {
                    this.onExportBackupDialogShow(id);
                },
            });
        };
        this.onDaysDialogShow = (mode) => {
            this.presenters.onDaysDialogActivate({
                selectedDays: this.form.getValue().dayOfWeek || [],
                mode,
                onDone: (modelValue) => {
                    this.form.model.update((value) => {
                        value.dayOfWeek = modelValue;
                    });
                },
            });
        };
        this.onExportLatestBackupDialogShow = () => {
            const { restorePointId } = this.model.getValue();
            this.onExportBackupDialogShow(restorePointId);
        };
        this.onSave = () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const isValid = yield this.isValid();
            if (!isValid) {
                return Promise.resolve();
            }
            this.presenters.savingState(true);
            const modelValue = this.form.getValue();
            try {
                yield this.loaders.saveLoadableModel.load(modelValue);
                this.updateBackupStatus(ConfigurationBackupModel_1.CONFIGURATION_BACKUP_STATUS.success);
                const newModelValue = this.form.getValue();
                (_b = (_a = this.listeners) === null || _a === void 0 ? void 0 : _a.onSaveDone) === null || _b === void 0 ? void 0 : _b.call(_a, newModelValue);
                this.presenters.savedState(true);
            }
            catch (error) {
                this.presenters.onCatchError(error);
                this.updateBackupStatus(ConfigurationBackupModel_1.CONFIGURATION_BACKUP_STATUS.failed);
            }
            finally {
                this.form.makeValueInitial();
                this.presenters.savingState(false);
            }
        });
        this.onBackupDialogShow = () => {
            const { isScheduleEnabled, repositoryId, repositoryName } = this.form.getValue();
            const initialRepository = isScheduleEnabled
                ? {
                    id: repositoryId,
                    name: repositoryName,
                }
                : undefined;
            this.presenters.onCreateManualBackupActivate({
                initialRepository,
                onDone: () => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b;
                    (_b = (_a = this.listeners).onBackupDone) === null || _b === void 0 ? void 0 : _b.call(_a);
                    this.presenters.onGoToSessionLogsDialogShow(this.config.goToSessionLogsDialogMessage);
                }),
            });
        };
        this.onExport = (restorePointId, password, hint) => __awaiter(this, void 0, void 0, function* () {
            var _c, _d;
            this.presenters.loadingState(true);
            try {
                yield this.loaders.exportLoadableModel.load({ restorePointId, password, hint });
                const { error } = this.loaders.exportLoadableModel.getMeta();
                if (error) {
                    return;
                }
                (_d = (_c = this.listeners) === null || _c === void 0 ? void 0 : _c.onExportDone) === null || _d === void 0 ? void 0 : _d.call(_c, restorePointId, password, hint);
            }
            catch (error) {
                this.presenters.onCatchError(error);
            }
            finally {
                this.presenters.loadingState(false);
            }
        });
        this.model = model;
        this.loaders = loaders;
        this.config = config;
        this.presenters = presenters;
        this.listeners = listeners;
        this.form = new core_1.Form(this.model, {
            validationSchema: {
                repositoryId: [core_1.conditions({ isScheduleEnabled: true }, core_1.required())],
                retentionCount: [
                    core_1.conditions({ isScheduleEnabled: true }, core_1.required()),
                    core_1.conditions({ isScheduleEnabled: true }, core_1.minNumber(ConfigurationBackupModel_1.ConfigurationBackupModel.RETENTION_COUNT_MIN)),
                    core_1.conditions({ isScheduleEnabled: true }, core_1.maxNumber(ConfigurationBackupModel_1.ConfigurationBackupModel.RETENTION_COUNT_MAX)),
                ],
                dayOfWeek: [
                    core_1.conditions({ isScheduleEnabled: true, dailyOfKind: core_2.DAILY_OF_KIND.selectedDays }, core_1.required()),
                ],
            },
        });
    }
    didActivate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.subscriptions.add(this.model.fieldChanges('dailyOfKind').subscribe(({ value }) => {
                if (ConfigurationBackupModel_1.ConfigurationBackupModel.isDailyOfKindEveryday(value)) {
                    this.model.update((modelValue) => {
                        modelValue.dayOfWeek = ConfigurationBackupModel_1.ConfigurationBackupModel.EVERYDAY;
                    });
                }
                if (ConfigurationBackupModel_1.ConfigurationBackupModel.isDailyOfKindWeekdays(value)) {
                    this.model.update((modelValue) => {
                        modelValue.dayOfWeek = ConfigurationBackupModel_1.ConfigurationBackupModel.WEEKDAYS;
                    });
                }
            }));
            yield this.loadCurrentSettings();
        });
    }
    willDeactivate() {
        return __awaiter(this, void 0, void 0, function* () {
            this.loaders.currentSettingsLoadableModel.destroy();
            this.loaders.exportLoadableModel.destroy();
            this.loaders.saveLoadableModel.destroy();
            this.subscriptions.unsubscribe();
        });
    }
    onExportBackupDialogShow(restorePointId) {
        this.presenters.onExportLatestBackupActivate({
            restorePointId,
            onDone: (password, hint) => __awaiter(this, void 0, void 0, function* () {
                yield this.onExport(restorePointId, password, hint);
            }),
            onClose: () => {
                this.presenters.loadingState(false);
            },
        });
    }
    updateBackupStatus(status) {
        const { isScheduleEnabled } = this.form.getValue();
        if (isScheduleEnabled) {
            this.model.update((value) => {
                value.status = status;
            });
        }
    }
    isValid() {
        return __awaiter(this, void 0, void 0, function* () {
            const isFormValid = this.form.validateAndDisplayErrors();
            return Promise.resolve(isFormValid);
        });
    }
    loadCurrentSettings() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                this.presenters.loadingState(true);
                yield this.loaders.currentSettingsLoadableModel.load();
                const { error } = this.loaders.exportLoadableModel.getMeta();
                if (error) {
                    return;
                }
                const currentValue = this.loaders.currentSettingsLoadableModel.getValue();
                this.model.setValue(currentValue);
            }
            catch (error) {
                this.presenters.onCatchError(error);
                this.model.update((value) => {
                    value.status = ConfigurationBackupModel_1.CONFIGURATION_BACKUP_STATUS.failed;
                });
            }
            finally {
                this.form.makeValueInitial();
                this.presenters.loadingState(false);
            }
        });
    }
    static isManagedVBR(mode) {
        return mode === types_1.CONFIGURATION_BACKUP_MODE.managedVBR;
    }
    static isManagedPlatform(mode) {
        return mode === types_1.CONFIGURATION_BACKUP_MODE.managedPlatform;
    }
}
exports.ConfigurationBackupInteractor = ConfigurationBackupInteractor;
//# sourceMappingURL=ConfigurationBackupInteractor.js.map